/**
 * GTM Tracking Utilities for Homebot Sign-up Flow
 * 
 * This module provides reusable tracking functions for Google Tag Manager integration.
 * All functions check for GTM availability before executing.
 */

import { getPlanDetails } from '../planDetails';

/**
 * Core GTM event tracking function
 * @param {string} eventName - The GTM event name
 * @param {Object} parameters - Additional event parameters
 * @param {Object} formContext - Current form context (plan, paymentMethod, etc.)
 */
export const trackFormEvent = (eventName, parameters = {}, formContext = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      event_category: 'signup_form',
      page_title: 'Homebot Signup',
      plan_selected: formContext.plan,
      payment_method: formContext.paymentMethod,
      subscription_length: formContext.subscriptionLength,
      ...parameters
    });
  }
};

/**
 * Track field interaction events (focus, blur, change, error)
 * @param {string} fieldName - Name of the field
 * @param {string} interactionType - Type of interaction ('focus', 'blur', 'change', 'error')
 * @param {string|null} value - Field value (truncated for privacy)
 * @param {Object} formContext - Current form context
 */
export const trackFieldInteraction = (fieldName, interactionType, value = null, formContext = {}) => {
  trackFormEvent('field_interaction', {
    field_name: fieldName,
    interaction_type: interactionType,
    field_value: value ? value.toString().substring(0, 50) : null,
    form_section: getCurrentFormSection(formContext.plan)
  }, formContext);
};

/**
 * Track form progression through different sections
 * @param {string} sectionName - Name of the form section
 * @param {string} actionType - Action type ('enter', 'complete', 'error')
 * @param {Object} formState - Complete form state for completion calculation
 * @param {Object} formContext - Current form context
 */
export const trackFormProgression = (sectionName, actionType = 'enter', formState = {}, formContext = {}) => {
  trackFormEvent('form_progression', {
    section_name: sectionName,
    action_type: actionType,
    form_completion_percentage: calculateFormCompletionPercentage(formState)
  }, formContext);
};

/**
 * Track form errors (validation, API, etc.)
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message (truncated for privacy)
 * @param {string|null} fieldName - Field name if applicable
 * @param {Object} formContext - Current form context
 */
export const trackError = (errorType, errorMessage, fieldName = null, formContext = {}) => {
  trackFormEvent('form_error', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100),
    field_name: fieldName,
    form_section: getCurrentFormSection(formContext.plan)
  }, formContext);
};

/**
 * Track promo code interactions
 * @param {string} eventType - Type of promo code event ('apply_attempt', 'apply_success', 'apply_error', 'remove')
 * @param {string} promoCode - The promo code
 * @param {Object|null} result - Validation result if applicable
 * @param {Object} formContext - Current form context
 */
export const trackPromoCodeEvent = (eventType, promoCode, result = null, formContext = {}) => {
  trackFormEvent('promo_code_interaction', {
    event_type: eventType,
    promo_code_length: promoCode ? promoCode.length : 0,
    discount_type: result?.discountInfo?.type || null,
    discount_amount: result?.discount || null
  }, formContext);
};

/**
 * Track successful conversion
 * @param {Object} conversionData - Additional conversion data
 * @param {Object} formContext - Current form context
 * @param {number} totalDueToday - Total amount due
 * @param {number} monetaryDiscount - Total discount amount
 * @param {Array} appliedPromoCodes - Applied promo codes
 * @param {Function} getFullPlanCode - Function to get full plan code
 */
export const trackConversion = (
  conversionData,
  formContext,
  totalDueToday,
  monetaryDiscount,
  appliedPromoCodes,
  getFullPlanCode
) => {
  trackFormEvent('signup_conversion', {
    value: totalDueToday,
    currency: 'USD',
    plan_code: getFullPlanCode(formContext.plan, formContext.subscriptionLength),
    payment_method: formContext.paymentMethod,
    has_promo_codes: appliedPromoCodes.length > 0,
    promo_codes_count: appliedPromoCodes.length,
    total_discount: monetaryDiscount,
    customer_type: getPlanDetails(formContext.plan).customerType,
    ...conversionData
  }, formContext);
};

/**
 * Get current form section based on plan
 * @param {string} plan - Current plan selection
 * @returns {string} Form section identifier
 */
export const getCurrentFormSection = (plan) => {
  if (plan === 'freemium-starter' || plan === 'hb-agent-solo' || plan === 'hb-agent-partner' || plan === 'hb-agent-team') {
    return 'real_estate_signup';
  } else if (plan === 'hb-lender-starter' || plan === 'hb-lender-pro' || plan === 'fairway-individual' || plan === 'fairway-individual-starter') {
    return 'lender_signup';
  } else if (plan === 'hb-insurance-agent-pro') {
    return 'insurance_agent_signup';
  } else if (plan === 'hb-title-agent-pro') {
    return 'title_agent_signup';
  }
  return 'unknown';
};

/**
 * Calculate form completion percentage
 * @param {Object} formState - Complete form state
 * @returns {number} Completion percentage (0-100)
 */
export const calculateFormCompletionPercentage = (formState) => {
  const {
    firstName = '',
    lastName = '',
    email = '',
    paymentMethod = '',
    termsAccepted = false,
    plan = '',
    nmlsId = '',
    stateLicense = '',
    subscriptionLength = '',
    cardFirstName = '',
    cardLastName = '',
    billingAddress = '',
    billingCity = '',
    billingState = '',
    billingZip = '',
    accountType = '',
    routingNumber = '',
    accountNumber = '',
    accountNumberConfirmation = ''
  } = formState;

  const requiredFields = {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    email: email.trim(),
    paymentMethod: paymentMethod,
    termsAccepted: termsAccepted
  };

  // Add plan-specific required fields
  if (plan === 'hb-lender-starter' || plan === 'hb-lender-pro' || plan === 'fairway-individual' || plan === 'fairway-individual-starter') {
    requiredFields.nmlsId = nmlsId.trim();
    if (plan !== 'fairway-individual' && plan !== 'fairway-individual-starter') {
      requiredFields.subscriptionLength = subscriptionLength;
    }
  } else if (plan === 'freemium-starter' || plan === 'hb-agent-solo' || plan === 'hb-agent-partner' || plan === 'hb-agent-team') {
    requiredFields.stateLicense = stateLicense.trim();
  }

  // Add payment-specific required fields
  if (paymentMethod === 'card') {
    requiredFields.cardFirstName = cardFirstName.trim();
    requiredFields.cardLastName = cardLastName.trim();
    requiredFields.billingAddress = billingAddress.trim();
    requiredFields.billingCity = billingCity.trim();
    requiredFields.billingState = billingState;
    requiredFields.billingZip = billingZip.trim();
  } else if (paymentMethod === 'bank') {
    requiredFields.accountType = accountType;
    requiredFields.routingNumber = routingNumber.trim();
    requiredFields.accountNumber = accountNumber.trim();
    requiredFields.accountNumberConfirmation = accountNumberConfirmation.trim();
  }

  const completedFields = Object.values(requiredFields).filter(value => 
    value && value !== '' && value !== false
  ).length;
  
  return Math.round((completedFields / Object.keys(requiredFields).length) * 100);
};

/**
 * Enhanced tracking functions for specific use cases
 */

/**
 * Track page load with initial context
 * @param {Object} initialContext - Initial form context
 */
export const trackPageLoad = (initialContext) => {
  trackFormEvent('form_loaded', {
    initial_plan: initialContext.plan,
    has_sponsor_id: !!initialContext.sponsorId,
    has_promo_code: !!initialContext.promoCode,
    user_agent: navigator.userAgent.substring(0, 100)
  }, initialContext);
  
  trackFormProgression('form_start', 'enter', {}, initialContext);
};

/**
 * Track plan selection with additional context
 * @param {string} selectedPlan - Newly selected plan
 * @param {string} previousPlan - Previously selected plan
 * @param {Object} formContext - Current form context
 */
export const trackPlanSelection = (selectedPlan, previousPlan, formContext) => {
  let planType = 'real_estate';
  if (selectedPlan.includes('lender')) {
    planType = 'lender';
  } else if (selectedPlan.includes('insurance')) {
    planType = 'insurance_agent';
  } else if (selectedPlan.includes('title')) {
    planType = 'title_agent';
  }
  
  trackFormEvent('plan_selection', {
    selected_plan: selectedPlan,
    plan_type: planType,
    previous_plan: previousPlan
  }, formContext);
};

/**
 * Track payment method selection
 * @param {string} selectedMethod - Newly selected payment method
 * @param {string} previousMethod - Previously selected payment method
 * @param {Object} formContext - Current form context
 */
export const trackPaymentMethodSelection = (selectedMethod, previousMethod, formContext) => {
  trackFormEvent('payment_method_selection', {
    selected_method: selectedMethod,
    previous_method: previousMethod
  }, formContext);
};

/**
 * Track form submission attempt
 * @param {Object} formState - Complete form state
 * @param {Object} formContext - Current form context
 */
export const trackFormSubmissionAttempt = (formState, formContext) => {
  trackFormEvent('form_submission_attempt', {
    form_completion_percentage: calculateFormCompletionPercentage(formState)
  }, formContext);
};

/**
 * Track form submission failure
 * @param {string} failureReason - Reason for failure
 * @param {Object} validationErrors - Validation errors object
 * @param {Object} formContext - Current form context
 */
export const trackFormSubmissionFailure = (failureReason, validationErrors, formContext) => {
  trackFormEvent('form_submission_failed', {
    failure_reason: failureReason,
    error_count: Object.keys(validationErrors).length,
    error_fields: Object.keys(validationErrors)
  }, formContext);
};

/**
 * Track signup failure
 * @param {Error} error - Error object
 * @param {string} failurePoint - Where the failure occurred
 * @param {Object} formContext - Current form context
 */
export const trackSignupFailure = (error, failurePoint, formContext) => {
  trackFormEvent('signup_failed', {
    error_message: error.toString().substring(0, 100),
    failure_point: failurePoint,
    payment_method: formContext.paymentMethod
  }, formContext);
};

/**
 * Track terms acceptance
 * @param {boolean} accepted - Whether terms were accepted
 * @param {Object} formContext - Current form context
 */
export const trackTermsAcceptance = (accepted, formContext) => {
  trackFormEvent('terms_acceptance', {
    accepted: accepted
  }, formContext);
};

/**
 * Track promo code section opened
 * @param {Object} formContext - Current form context
 */
export const trackPromoCodeSectionOpened = (formContext) => {
  trackFormEvent('promo_code_section_opened', {}, formContext);
}; 
